import { isNil, path } from 'ramda';
import { IMarkedListData, TObjectType, TPlaceholder, TPlaceholderParams } from './types';
import { PLACEHOLDERS_DICT } from './constants';
import { IMarkedList } from '../../../types/seo';
import { plural, shortenNumber } from '@cian/utils';
import {
  AREA_VARIANTS,
  COTTAGE_VARIANTS,
  HOUSE_VARIANTS,
  LAND_VARIANTS,
  SHORTEN_NUMBER_OPTIONS,
  TOWNHOUSE_VARIANTS,
} from '../../../interfaces/villages';
import { formatNumber } from '../../helpers';

/**
 * Заменяет плейсхолдеры в шаблонах на реальные значения
 */
export const formatMarkedList = (markedList: IMarkedList, data: IMarkedListData): IMarkedList => {
  const items = markedList.items.map(item => {
    return {
      ...item,
      title: templateFormatter(item.title, data),
      content: templateFormatter(item.content, data),
    };
  });

  const schema = markedList.schema ? templateFormatter(markedList.schema, data) : markedList.schema;

  return { items, schema };
};

function templateFormatter(template: string, data: IMarkedListData): string {
  return template.replace(/\[.+?\]/gi, (match: TPlaceholder) => templateReplacer(match, data));
}

/**
 * Заменяет плейсхолдер в шаблоне.
 * Если значение определено, то оно заменяет плейхолдер.
 * Если значение равно `undefined`, то оставляет исходный плейсхолдер.
 * Если значение равно `null`, то заменяет на ноль.
 */
function templateReplacer(placeholder: TPlaceholder, data: IMarkedListData): string {
  const placeholderParams = PLACEHOLDERS_DICT.get(placeholder);

  if (!placeholderParams) {
    return placeholder;
  }

  const value = path<string | number | null>(placeholderParams.path, data);

  if (value === undefined) {
    return placeholder;
  }

  return formatValue(value, placeholderParams);
}

function formatValue(value: string | number | null, { metricType, objectType }: TPlaceholderParams): string {
  switch (metricType) {
    case 'price':
      return `${shortenNumber(Number(value), SHORTEN_NUMBER_OPTIONS)} руб.`;
    case 'area': {
      let result = formatNumber(Number(value));
      const unit = getAreaUnit(Number(value), objectType);
      if (unit) {
        result += ` ${unit}`;
      }

      return result;
    }
    case 'count': {
      let result = String(value);
      const unit = getCountUnit(Number(value), objectType);
      if (unit) {
        result += ` ${unit}`;
      }

      return result;
    }
    case 'anyNumber': {
      return String(Number(value));
    }
    case 'anyString':
    default: {
      return isNil(value) ? '' : String(value);
    }
  }
}

function getAreaUnit(value: number, objectType: TObjectType | undefined): string | null {
  switch (objectType) {
    case 'land':
      return plural(value, AREA_VARIANTS);
    case 'house':
    case 'townhouse':
    case 'cottage':
      return 'м²';
    default:
      return null;
  }
}

function getCountUnit(value: number, objectType: TObjectType | undefined): string | null {
  switch (objectType) {
    case 'house':
      return plural(value, HOUSE_VARIANTS);
    case 'townhouse':
      return plural(value, TOWNHOUSE_VARIANTS);
    case 'cottage':
      return plural(value, COTTAGE_VARIANTS);
    case 'land':
      return plural(value, LAND_VARIANTS);
    default:
      return null;
  }
}
